import { SurfaceFunction, SurfaceFunctionKey } from '../types';

export const SURFACE_FNS: Record<SurfaceFunctionKey, SurfaceFunction> = {
  convex_squircle: (x: number) => Math.pow(1 - Math.pow(1 - x, 4), 0.25),
  convex_circle: (x: number) => Math.sqrt(1 - (1 - x) * (1 - x)),
  concave: (x: number) => 1 - Math.sqrt(1 - (1 - x) * (1 - x)),
  lip: (x: number) => {
    const convex = Math.pow(1 - Math.pow(1 - Math.min(x * 2, 1), 4), 0.25);
    const concave = 1 - Math.sqrt(1 - (1 - x) * (1 - x)) + 0.1;
    const t = 6 * x ** 5 - 15 * x ** 4 + 10 * x ** 3;
    return convex * (1 - t) + concave * t;
  },
};

export function calculateRefractionProfile(
  glassThickness: number,
  bezelWidth: number,
  heightFn: SurfaceFunction,
  ior: number,
  samples = 128
): Float64Array {
  const eta = 1 / ior;
  function refract(nx: number, ny: number): [number, number] | null {
    const dot = ny;
    const k = 1 - eta * eta * (1 - dot * dot);
    if (k < 0) return null;
    const sq = Math.sqrt(k);
    return [-(eta * dot + sq) * nx, eta - (eta * dot + sq) * ny];
  }

  const profile = new Float64Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = i / samples;
    const y = heightFn(x);
    const dx = x < 1 ? 0.0001 : -0.0001;
    const y2 = heightFn(x + dx);
    const deriv = (y2 - y) / dx;
    const mag = Math.sqrt(deriv * deriv + 1);
    const ref = refract(-deriv / mag, -1 / mag);
    if (!ref) {
      profile[i] = 0;
      continue;
    }
    profile[i] = ref[0] * ((y * bezelWidth + glassThickness) / ref[1]);
  }
  return profile;
}

export function generateDisplacementMap(
  w: number,
  h: number,
  radius: number,
  bezelWidth: number,
  profile: Float64Array,
  maxDisp: number
): string {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) return '';

  const img = ctx.createImageData(w, h);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 128;
    d[i + 1] = 128;
    d[i + 2] = 0;
    d[i + 3] = 255;
  }

  const r = Math.max(Math.min(radius, Math.floor(Math.min(w, h) / 2)), 1);
  const rSq = r * r;
  const r1Sq = (r + 1) ** 2;
  const clampedBezel = Math.max(Math.min(bezelWidth, r - 1), 1);
  const rBSq = Math.max(r - clampedBezel, 0) ** 2;
  const wB = Math.max(w - r * 2, 0);
  const hB = Math.max(h - r * 2, 0);
  const S = profile.length;

  for (let y1 = 0; y1 < h; y1++) {
    for (let x1 = 0; x1 < w; x1++) {
      const x = x1 < r ? x1 - r : x1 >= w - r ? x1 - r - wB : 0;
      const y = y1 < r ? y1 - r : y1 >= h - r ? y1 - r - hB : 0;
      if (x === 0 && y === 0) continue;
      const dSq = x * x + y * y;
      if (dSq > r1Sq || dSq < rBSq) continue;
      const dist = Math.sqrt(dSq);
      if (dist < 0.0001) continue;
      const fromSide = r - dist;
      const op = dSq < rSq ? 1 : 1 - (dist - Math.sqrt(rSq)) / (Math.sqrt(r1Sq) - Math.sqrt(rSq));
      if (op <= 0) continue;
      const cos = x / dist,
        sin = y / dist;
      const bi = Math.min(((fromSide / clampedBezel) * S) | 0, S - 1);
      const disp = profile[bi] || 0;
      const dX = (-cos * disp) / maxDisp,
        dY = (-sin * disp) / maxDisp;
      const idx = (y1 * w + x1) * 4;
      d[idx] = (128 + dX * 127 * op + 0.5) | 0;
      d[idx + 1] = (128 + dY * 127 * op + 0.5) | 0;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL();
}

export function generateSpecularMap(
  w: number,
  h: number,
  radius: number,
  bezelWidth: number,
  angle?: number
): string {
  angle = angle != null ? angle : Math.PI / 3;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) return '';

  const img = ctx.createImageData(w, h);
  const d = img.data;
  d.fill(0);

  const r = Math.max(Math.min(radius, Math.floor(Math.min(w, h) / 2)), 1);
  const rSq = r * r;
  const r1Sq = (r + 1) ** 2;
  const clampedBezel = Math.max(Math.min(bezelWidth, r - 1), 1);
  const rBSq = Math.max(r - clampedBezel, 0) ** 2;
  const wB = Math.max(w - r * 2, 0);
  const hB = Math.max(h - r * 2, 0);
  const sv = [Math.cos(angle), Math.sin(angle)];

  for (let y1 = 0; y1 < h; y1++) {
    for (let x1 = 0; x1 < w; x1++) {
      const x = x1 < r ? x1 - r : x1 >= w - r ? x1 - r - wB : 0;
      const y = y1 < r ? y1 - r : y1 >= h - r ? y1 - r - hB : 0;
      if (x === 0 && y === 0) continue;
      const dSq = x * x + y * y;
      if (dSq > r1Sq || dSq < rBSq) continue;
      const dist = Math.sqrt(dSq);
      if (dist < 0.0001) continue;
      const fromSide = r - dist;
      const op = dSq < rSq ? 1 : 1 - (dist - Math.sqrt(rSq)) / (Math.sqrt(r1Sq) - Math.sqrt(rSq));
      if (op <= 0) continue;
      const cos = x / dist,
        sin = -y / dist;
      const dot = Math.abs(cos * sv[0] + sin * sv[1]);
      const edge = Math.sqrt(Math.max(0, 1 - (1 - fromSide) ** 2));
      const coeff = dot * edge;
      const col = (255 * coeff) | 0;
      const alpha = (col * coeff * op) | 0;
      const idx = (y1 * w + x1) * 4;
      d[idx] = col;
      d[idx + 1] = col;
      d[idx + 2] = col;
      d[idx + 3] = alpha;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c.toDataURL();
}

export function buildSvgFilterMarkup(
  filterId: string,
  w: number,
  h: number,
  radius: number,
  glassThickness: number,
  bezelWidth: number,
  ior: number,
  scaleRatio: number,
  blurAmount: number,
  specularOpacity: number,
  specularSaturation: number,
  surfaceKey: SurfaceFunctionKey = 'convex_squircle'
): string {
  const heightFn = SURFACE_FNS[surfaceKey] || SURFACE_FNS.convex_squircle;
  const clampedRadius = Math.max(Math.min(radius, Math.floor(Math.min(w, h) / 2)), 1);
  const clampedBezel = Math.max(Math.min(bezelWidth, clampedRadius - 1), 1);

  const profile = calculateRefractionProfile(glassThickness, clampedBezel, heightFn, ior, 128);
  const maxDisp = Math.max(...Array.from(profile).map(Math.abs)) || 1;
  const specBezel = Math.max(Math.min(clampedBezel * 0.8, clampedRadius - 1), 1);
  const dispUrl = generateDisplacementMap(w, h, clampedRadius, clampedBezel, profile, maxDisp);
  
  // Cap displacement scale to the bezel width so refraction stays strictly within the glass boundary
  const scale = Math.min(maxDisp * scaleRatio, clampedBezel * 1.0);

  if (specularOpacity > 0.01) {
    const specUrl = generateSpecularMap(w, h, clampedRadius, specBezel);
    return `
    <filter id="${filterId}" x="0%" y="0%" width="100%" height="100%" color-interpolation-filters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation="${blurAmount}" result="blurred_source" />
        <feImage href="${dispUrl}" xlink:href="${dispUrl}" preserveAspectRatio="none" result="disp_map" />
        <feDisplacementMap in="blurred_source" in2="disp_map"
            scale="${scale}" xChannelSelector="R" yChannelSelector="G"
            result="displaced" />
        <feColorMatrix in="displaced" type="saturate" values="${specularSaturation}" result="displaced_sat" />
        <feImage href="${specUrl}" xlink:href="${specUrl}" preserveAspectRatio="none" result="spec_layer" />
        <feComposite in="displaced_sat" in2="spec_layer" operator="in" result="spec_masked" />
        <feComponentTransfer in="spec_layer" result="spec_faded">
            <feFuncA type="linear" slope="${specularOpacity}" />
        </feComponentTransfer>
        <feBlend in="spec_masked" in2="displaced" mode="normal" result="with_sat" />
        <feBlend in="spec_faded" in2="with_sat" mode="normal" />
    </filter>
  `;
  }

  return `
    <filter id="${filterId}" x="0%" y="0%" width="100%" height="100%" color-interpolation-filters="sRGB">
        <feGaussianBlur in="SourceGraphic" stdDeviation="${blurAmount}" result="blurred_source" />
        <feImage href="${dispUrl}" xlink:href="${dispUrl}" preserveAspectRatio="none" result="disp_map" />
        <feDisplacementMap in="blurred_source" in2="disp_map"
            scale="${scale}" xChannelSelector="R" yChannelSelector="G"
            result="displaced" />
    </filter>
  `;
}
